import { Children, forwardRef, isValidElement, useLayoutEffect, useRef, useState, type ButtonHTMLAttributes, type AnchorHTMLAttributes, type HTMLAttributes, type ReactNode, type Ref } from 'react';
import { createPortal } from 'react-dom';
import type { LucideIcon } from 'lucide-react';
import './actions.css';

type ActionProps = { icon: LucideIcon; children: ReactNode; label?: string; suffix?: ReactNode };
type ActionElement = HTMLButtonElement | HTMLAnchorElement;

function actionText(children: ReactNode): string {
  return Children.toArray(children).map(child => typeof child === 'string' || typeof child === 'number' ? String(child) : isValidElement<{ children?: ReactNode }>(child) ? actionText(child.props.children) : '').join(' ').trim();
}

function number(value: string) { return Number.parseFloat(value) || 0; }

function expandedWidth(element: HTMLElement) {
  if (!element.classList.contains('action-button')) return element.getBoundingClientRect().width;
  const style = getComputedStyle(element);
  const label = element.querySelector<HTMLElement>('.action-label');
  const icon = element.querySelector<HTMLElement>('.action-icon');
  if (!label || !icon) return element.getBoundingClientRect().width;
  const suffixWidth = Array.from(element.children).filter(child => child !== label && child !== icon).reduce((width, child) => width + child.getBoundingClientRect().width + number(style.columnGap), 0);
  return Math.max(number(style.minWidth), label.scrollWidth + icon.getBoundingClientRect().width + suffixWidth + number(style.columnGap) + number(style.paddingLeft) + number(style.paddingRight) + number(style.borderLeftWidth) + number(style.borderRightWidth));
}

export function actionLabelsFit(available: number, widths: number[], gap: number) {
  return widths.reduce((total, width) => total + width, 0) + Math.max(0, widths.length - 1) * gap <= available;
}

function observeLayout(element: HTMLElement, update: () => void) {
  let frame = 0;
  let alive = true;
  const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(update); };
  const resize = new ResizeObserver(schedule);
  resize.observe(element);
  const mutation = new MutationObserver(schedule);
  mutation.observe(element, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
  for (let ancestor = element.parentElement; ancestor; ancestor = ancestor.parentElement) mutation.observe(ancestor, { attributes: true, attributeFilter: ['class', 'style', 'dir', 'lang'] });
  document.fonts?.ready.then(() => { if (alive) schedule(); });
  document.fonts?.addEventListener('loadingdone', schedule);
  window.addEventListener('resize', schedule);
  update();
  return () => { alive = false; cancelAnimationFrame(frame); resize.disconnect(); mutation.disconnect(); document.fonts?.removeEventListener('loadingdone', schedule); window.removeEventListener('resize', schedule); };
}

export function ActionGroup({ children, className = '', ...props }: HTMLAttributes<HTMLDivElement>) {
  const element = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const group = element.current!;
    return observeLayout(group, () => {
      const style = getComputedStyle(group);
      const children = Array.from(group.children).filter((child): child is HTMLElement => child instanceof HTMLElement && child.getClientRects().length > 0);
      if (style.display === 'grid' || style.flexDirection === 'column') {
        group.dataset.compact = 'false';
        for (const child of children.filter(child => child.classList.contains('action-button'))) child.dataset.actionCompact = String(expandedWidth(child) > child.getBoundingClientRect().width + .5);
        return;
      }
      const widths = children.map(child => { const style = getComputedStyle(child); return expandedWidth(child) + number(style.marginLeft) + number(style.marginRight); });
      const available = group.clientWidth - number(style.paddingLeft) - number(style.paddingRight);
      group.dataset.compact = String(!actionLabelsFit(available, widths, number(style.columnGap)));
    });
  }, []);
  return <div {...props} ref={element} className={`action-group ${className}`} data-action-group="" data-compact="false">{children}</div>;
}

function assignRef<Element>(ref: Ref<Element> | undefined, value: Element | null) {
  if (typeof ref === 'function') return ref(value);
  if (ref) ref.current = value;
}

function useActionLayout(element: { current: ActionElement | null }, label: string) {
  useLayoutEffect(() => {
    const button = element.current;
    const parent = button?.parentElement;
    if (!button || !parent || parent.hasAttribute('data-action-group')) return;
    if (parent.closest('[data-preserve-action-labels]')) {
      button.dataset.actionCompact = 'false';
      return;
    }
    return observeLayout(parent, () => {
      if (!button.getClientRects().length || !parent.getClientRects().length) return;
      const style = getComputedStyle(parent);
      const available = parent.clientWidth - number(style.paddingLeft) - number(style.paddingRight);
      button.dataset.actionCompact = String(expandedWidth(button) > available);
    });
  }, [label]);
}

function Tooltip({ anchor, children }: { anchor: ActionElement; children: string }) {
  const element = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const tooltip = element.current!;
    tooltip.showPopover();
    const place = () => {
      const bounds = anchor.getBoundingClientRect();
      const width = tooltip.offsetWidth;
      tooltip.style.left = `${Math.max(8, Math.min(bounds.left + (bounds.width - width) / 2, innerWidth - width - 8))}px`;
      tooltip.style.top = `${bounds.top >= tooltip.offsetHeight + 12 ? bounds.top - tooltip.offsetHeight - 8 : Math.min(bounds.bottom + 8, innerHeight - tooltip.offsetHeight - 8)}px`;
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); if (tooltip.matches(':popover-open')) tooltip.hidePopover(); };
  }, [anchor]);
  return createPortal(<div ref={element} popover="manual" role="tooltip" className="action-tooltip">{children}</div>, document.body);
}

function compact(element: ActionElement) {
  return element.dataset.actionCompact === 'true' || element.parentElement?.dataset.compact === 'true';
}

export const ActionButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & ActionProps>(function ActionButton({ icon: Icon, children, label, suffix, className = '', onFocus, onBlur, onMouseEnter, onMouseLeave, ...props }, forwardedRef) {
  const element = useRef<HTMLButtonElement | null>(null);
  const [tooltip, setTooltip] = useState(false);
  const accessibleName = props['aria-label'] ?? label ?? actionText(children);
  useActionLayout(element, accessibleName);
  return <><button {...props} ref={node => { element.current = node; return assignRef(forwardedRef, node); }} className={`action-button ${className}`} aria-label={accessibleName} title={props.title ?? accessibleName} onFocus={event => { setTooltip(compact(event.currentTarget)); onFocus?.(event); }} onBlur={event => { setTooltip(false); onBlur?.(event); }} onMouseEnter={event => { setTooltip(compact(event.currentTarget)); onMouseEnter?.(event); }} onMouseLeave={event => { setTooltip(false); onMouseLeave?.(event); }}><Icon className="action-icon" size={20} aria-hidden="true" /><span className="action-label" aria-hidden="true">{children}</span>{suffix}</button>{tooltip && element.current && <Tooltip anchor={element.current}>{accessibleName}</Tooltip>}</>;
});

export const ActionLink = forwardRef<HTMLAnchorElement, AnchorHTMLAttributes<HTMLAnchorElement> & ActionProps>(function ActionLink({ icon: Icon, children, label, suffix, className = '', onFocus, onBlur, onMouseEnter, onMouseLeave, ...props }, forwardedRef) {
  const element = useRef<HTMLAnchorElement | null>(null);
  const [tooltip, setTooltip] = useState(false);
  const accessibleName = props['aria-label'] ?? label ?? actionText(children);
  useActionLayout(element, accessibleName);
  return <><a {...props} ref={node => { element.current = node; return assignRef(forwardedRef, node); }} className={`button action-button ${className}`} aria-label={accessibleName} title={props.title ?? accessibleName} onFocus={event => { setTooltip(compact(event.currentTarget)); onFocus?.(event); }} onBlur={event => { setTooltip(false); onBlur?.(event); }} onMouseEnter={event => { setTooltip(compact(event.currentTarget)); onMouseEnter?.(event); }} onMouseLeave={event => { setTooltip(false); onMouseLeave?.(event); }}><Icon className="action-icon" size={20} aria-hidden="true" /><span className="action-label" aria-hidden="true">{children}</span>{suffix}</a>{tooltip && element.current && <Tooltip anchor={element.current}>{accessibleName}</Tooltip>}</>;
});
